import { format, parseISO } from "date-fns";

export const DATE_FORMATS = {
  /** HTML date input fields (yyyy-MM-dd) */
  INPUT: "yyyy-MM-dd",
  /** Standard display format (MM/dd/yyyy) */
  DISPLAY: "MM/dd/yyyy",
  /** Long display format (MMM d, yyyy) — e.g. "Jan 15, 2024" */
  DISPLAY_LONG: "MMM d, yyyy",
  /** Long display with time to seconds (MMM d, yyyy h:mm:ss a)
   *  — e.g. "Jan 15, 2024 10:30:45 AM". Use when sub-minute granularity
   *  matters (e.g. document processing timestamps, where total processing
   *  time is typically seconds). */
  DISPLAY_LONG_WITH_TIME: "MMM d, yyyy h:mm:ss a",
  /** Short display without year (MMM d) — e.g. "Jan 15" */
  DISPLAY_SHORT: "MMM d",
  /** Year only (yyyy) */
  YEAR: "yyyy",
  /** Month and year (MMM yyyy) */
  MONTH_YEAR: "MMM yyyy",
} as const;

/**
 * Formats an ISO date string into a human-readable format.
 * Uses parseISO to correctly handle date-only strings as local time (avoids timezone issues).
 *
 * @param value - ISO date string (e.g., "2024-01-15" or "2024-01-15T10:30:00Z")
 * @param dateFormat - date-fns format string (default: "MM/dd/yyyy")
 * @returns Formatted date string, or the original value if parsing fails
 *
 * @example
 * formatDate("2024-01-15") // "01/15/2024"
 * formatDate("2024-01-15", DATE_FORMATS.DISPLAY_LONG) // "Jan 15, 2024"
 */
export const formatDate = (value: string, dateFormat: string = DATE_FORMATS.DISPLAY) => {
  try {
    return format(parseISO(value), dateFormat);
  } catch {
    return value;
  }
};

/**
 * Formats a date to YYYY-MM-DD for HTML date input fields.
 * Handles both string and Date inputs.
 *
 * @param value - Date string, Date object, or null/undefined
 * @returns YYYY-MM-DD string for input fields, or empty string if invalid
 *
 * @example
 * toInputDate("2024-01-15T10:30:00Z") // "2024-01-15"
 * toInputDate(new Date(2024, 0, 15)) // "2024-01-15"
 * toInputDate(null) // ""
 */
export const toInputDate = (value?: string | Date | null): string => {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);
  return format(value, DATE_FORMATS.INPUT);
};

/**
 * Today's date as YYYY-MM-DD in the **reviewer's local calendar**.
 *
 * Use this — not `new Date().toISOString().slice(0, 10)` — whenever comparing
 * against a value from a native `<input type="date">`. `toISOString()` converts
 * to UTC, so for any negative-offset timezone (all of the US) an evening "today"
 * rolls forward to tomorrow's UTC date, putting the comparison frame a day ahead
 * of the calendar the reviewer actually sees. `date-fns` `format` uses local
 * time, keeping both sides of the comparison in the same frame.
 *
 * @example
 * todayLocalYMD() // "2026-06-08" (matches the date picker's notion of today)
 */
export const todayLocalYMD = (): string => format(new Date(), DATE_FORMATS.INPUT);

/**
 * Today's date as YYYY-MM-DD in **UTC**: the mgmt-api's frame for "today"
 * (`utcToday`, and the resolver's `current_date`). Use it only to mirror a
 * server rule, e.g. an expected trigger's review-by date (#7361): from 19:00 to
 * 24:00 CDT the local date is a day behind, so a local check passes a review-by
 * the API then rejects, or misses one it already treats as overdue (!4881
 * thread 30545). Dates the user picks for themselves stay local
 * (`todayLocalYMD`).
 */
export const todayUtcYMD = (): string => new Date().toISOString().slice(0, 10);

/**
 * Converts a YYYY-MM-DD date string into a noon-UTC RFC3339 datetime
 * suitable for posting to the mgmt-api. Noon UTC sidesteps timezone
 * date-shift bugs in either direction.
 *
 * Pair with {@link fromISO} as the `parse`/`format` props on react-admin's
 * `DateInput` whenever the value flows to a Go `time.Time` JSON field —
 * stdlib `time.Time` unmarshal rejects bare "YYYY-MM-DD" with
 * `cannot parse "" as "T"`.
 *
 * @example
 * toISO("1949-04-20") // "1949-04-20T12:00:00Z"
 * toISO("") // null
 * toISO(undefined) // null
 */
export const toISO = (v?: string): string | null => {
  if (!v) return null;
  const dateStr = String(v).substring(0, 10);
  return `${dateStr}T12:00:00Z`;
};

/**
 * Extracts the YYYY-MM-DD portion of a stored ISO datetime so a
 * native `<input type="date">` can render it.
 *
 * @example
 * fromISO("1949-04-20T12:00:00Z") // "1949-04-20"
 * fromISO(undefined) // ""
 */
export const fromISO = (v?: string): string => (v ? String(v).substring(0, 10) : "");

/**
 * Formats a date range from two optional ISO date strings.
 * Handles partial ranges gracefully with "From" or "Until" prefixes.
 *
 * @param startDate - Optional ISO start date string
 * @param endDate - Optional ISO end date string
 * @param dateFormat - date-fns format string (default: "MM/dd/yyyy")
 * @returns Formatted range string
 *
 * @example
 * formatDateRange("2024-01-01", "2024-12-31") // "01/01/2024 - 12/31/2024"
 * formatDateRange("2024-01-01", "2024-12-31", DATE_FORMATS.DISPLAY_LONG) // "Jan 1, 2024 - Dec 31, 2024"
 * formatDateRange("2024-01-01", undefined) // "From 01/01/2024"
 * formatDateRange(undefined, "2024-12-31") // "Until 12/31/2024"
 * formatDateRange(undefined, undefined) // "-"
 */
export const formatDateRange = (
  startDate?: string,
  endDate?: string,
  dateFormat: string = DATE_FORMATS.DISPLAY
): string => {
  try {
    const start = startDate ? format(parseISO(startDate), dateFormat) : null;
    const end = endDate ? format(parseISO(endDate), dateFormat) : null;

    if (start && end) return `${start} - ${end}`;
    if (start) return `From ${start}`;
    if (end) return `Until ${end}`;

    return "-";
  } catch {
    return "-";
  }
};

/**
 * Formats a year range from two optional ISO date strings.
 * Extracts years directly from strings to avoid timezone issues.
 * Returns a compact format suitable for chips and labels.
 *
 * @param start - Optional ISO start date string
 * @param end - Optional ISO end date string
 * @returns Year range string (e.g., "2024" or "2024–2025")
 *
 * @example
 * formatYearRange("2024-01-01", "2024-12-31") // "2024"
 * formatYearRange("2024-07-01", "2025-06-30") // "2024–2025"
 * formatYearRange("2024-01-01", undefined) // "2024"
 * formatYearRange(undefined, "2024-12-31") // "2024"
 * formatYearRange(undefined, undefined) // ""
 */
export const formatYearRange = (start?: string, end?: string) => {
  const startYear = start?.slice(0, 4);
  const endYear = end?.slice(0, 4);

  if (!startYear && !endYear) return "";
  if (!startYear) return endYear!;
  if (!endYear || startYear === endYear) return startYear;
  return `${startYear}-${endYear}`;
};

/**
 * Formats an optional date string to a short display (MMM d) — e.g. "Nov 14".
 * Returns "—" if the value is null/undefined/empty.
 *
 * @example
 * formatShortDate("2025-11-14") // "Nov 14"
 * formatShortDate(null) // "—"
 */
export const formatShortDate = (value?: string | null): string => {
  if (!value) return "—";
  try {
    return format(parseISO(value), DATE_FORMATS.DISPLAY_SHORT);
  } catch {
    return "—";
  }
};

/**
 * Formats an optional date string to a long display (MMM d, yyyy) — e.g. "Nov 14, 2025".
 * Returns "—" if the value is null/undefined/empty.
 *
 * @example
 * formatLongDate("2025-11-14") // "Nov 14, 2025"
 * formatLongDate(null) // "—"
 */
export const formatLongDate = (value?: string | null): string => {
  if (!value) return "—";
  try {
    return format(parseISO(value), DATE_FORMATS.DISPLAY_LONG);
  } catch {
    return "—";
  }
};

/**
 * Formats a date as a relative string for recent dates, falling back to short/long format.
 * - Today → "Today"
 * - Yesterday → "Yesterday"
 * - 2-6 days ago → "3d ago"
 * - 7+ days, same year → "Mar 10"
 * - Different year → "Jan 15, 2024"
 *
 * @example
 * formatRelativeDate("2026-03-25T10:00:00Z") // "Today" (if today is Mar 25, 2026)
 * formatRelativeDate("2026-03-20T10:00:00Z") // "5d ago"
 * formatRelativeDate("2025-11-14T10:00:00Z") // "Nov 14, 2025"
 */
export const formatRelativeDate = (value?: string | null): string => {
  if (!value) return "";
  try {
    const date = parseISO(value);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays <= 6) return `${diffDays}d ago`;

    // Same year → short, different year → long
    if (date.getFullYear() === now.getFullYear()) {
      return format(date, DATE_FORMATS.DISPLAY_SHORT);
    }
    return format(date, DATE_FORMATS.DISPLAY_LONG);
  } catch {
    return "";
  }
};
